import React, { useEffect, useState } from 'react';
import api from '../services/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import BrazilMap from './BrazilMap';
import './LinkStats.css';

const COLORS = ['#0ad596', '#12201b', '#0bc584', '#1a3d2e', '#2d5a47'];

const LinkStats = ({ link, stats: initialStats, getShortUrl, onBack }) => {
  const [stats, setStats] = useState(initialStats);
  const [loading, setLoading] = useState(!initialStats);

  useEffect(() => {
    // Se receber stats como prop, usar eles
    if (initialStats) {
      setStats(initialStats);
      setLoading(false);
    } else if (link?.id) {
      // Se não tiver stats, buscar
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link?.id, initialStats]);

  const fetchStats = async () => {
    if (!link?.id) return;
    
    setLoading(true);
    try {
      console.log('Buscando estatísticas para link:', link.id);
      const response = await api.get(`/stats/link/${link.id}`);
      console.log('Estatísticas recebidas:', response.data);
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      console.error('Detalhes:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Carregando estatísticas...</div>;
  }

  if (!stats) {
    return (
      <div className="loading">
        <p>Não foi possível carregar as estatísticas.</p>
        <button onClick={onBack} className="back-btn" style={{ marginTop: '20px' }}>
          ← Voltar
        </button>
      </div>
    );
  }

  return (
    <div className="link-stats">
      <div className="stats-header">
        <button onClick={onBack} className="back-btn">
          ← Voltar
        </button>
        <h2>Estatísticas do Link</h2>
      </div>

      <div className="link-info-card">
        <div className="link-info-item">
          <label>Apelido:</label>
          <div>{link.alias?.trim() ? link.alias : '—'}</div>
        </div>
        {link.is_whatsapp ? (
          <>
            <div className="link-info-item">
              <label>Destino:</label>
              <div>WhatsApp</div>
            </div>
            <div className="link-info-item">
              <label>Telefone:</label>
              <div>{link.whatsapp_phone}</div>
            </div>
            <div className="link-info-item">
              <label>Mensagem:</label>
              <div>{link.whatsapp_message}</div>
            </div>
          </>
        ) : (
          <div className="link-info-item">
            <label>URL Original:</label>
            <a href={link.original_url} target="_blank" rel="noopener noreferrer">
              {link.original_url}
            </a>
          </div>
        )}
        <div className="link-info-item">
          <label>Link Encurtado:</label>
          <div className="short-url-display">
            <code>{getShortUrl(link.short_code)}</code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(getShortUrl(link.short_code));
                alert('Link copiado!');
              }}
              className="copy-btn"
            >
              📋 Copiar
            </button>
          </div>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total de Acessos</h3>
          <div className="stat-value">{stats.total || 0}</div>
        </div>
      </div>

      {(stats.byRegion && stats.byRegion.length > 0) || (stats.byDate && stats.byDate.length > 0) ? (
        <div className="charts-row">
          {stats.byRegion && stats.byRegion.length > 0 && (
            <div className="chart-section chart-half">
              <h3>Acessos por Estado</h3>
              <BrazilMap accessData={stats.byRegion} />
            </div>
          )}

          {stats.byDate && stats.byDate.length > 0 && (
            <div className="chart-section chart-half">
              <h3>Acessos por Data (últimos 30 dias)</h3>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={stats.byDate}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" name="Acessos" fill="#0ad596" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      ) : null}

      {(() => {
        // Agrupar acessos por estado
        if (!stats.uniqueIPs || stats.uniqueIPs.length === 0) return null;
        
        const stateAccessMap = {};
        stats.uniqueIPs.forEach(ip => {
          const state = ip.region || 'Desconhecido';
          const country = ip.country || 'Desconhecido';
          const key = `${country} - ${state}`;
          
          if (!stateAccessMap[key]) {
            stateAccessMap[key] = {
              country,
              region: state,
              totalAccesses: 0
            };
          }
          stateAccessMap[key].totalAccesses += ip.access_count;
        });
        
        const stateAccesses = Object.values(stateAccessMap)
          .sort((a, b) => b.totalAccesses - a.totalAccesses);
        
        return (
          <div className="ip-section">
            <h3>Mapeamento de IPs</h3>
            <div className="ip-table">
              <table>
                <thead>
                  <tr>
                    <th>País</th>
                    <th>Estado</th>
                    <th>Total de Acessos</th>
                  </tr>
                </thead>
                <tbody>
                  {stateAccesses.map((state, index) => (
                    <tr key={index}>
                      <td>{state.country}</td>
                      <td>{state.region}</td>
                      <td>{state.totalAccesses}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {stats.recentAccesses && stats.recentAccesses.length > 0 && (
        <div className="recent-accesses">
          <h3>Últimos Acessos</h3>
          <div className="access-list">
            {stats.recentAccesses.slice(0, 10).map((access, index) => (
              <div key={index} className="access-item">
                <div className="access-info">
                  <span className="access-ip">{access.ip_address}</span>
                  <span className="access-location">
                    {[access.region, access.country]
                      .filter(Boolean)
                      .join(', ') || 'Localização desconhecida'}
                  </span>
                </div>
                <div className="access-time">
                  {new Date(access.accessed_at).toLocaleString('pt-BR')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkStats;

